import { invalidate } from '@react-three/fiber';
import { useCallback, useEffect, useMemo } from 'react';
import {
    BufferAttribute,
    BufferGeometry,
    Color,
    DoubleSide,
    InstancedMesh,
    Matrix4,
    MeshBasicMaterial,
    Vector3
} from 'three';
import { useShallow } from 'zustand/shallow';

import { useCreateClampDimensioning } from 'app/modules/wallforming/components/scenes/objects/dimensions/hooks/useCreateClampDimensioning';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { DimensionClamp } from 'app/modules/wallforming/components/scenes/objects/dimensions/types/dimensionClamp';

const MARKER_OFFSET = 0.008;
const THICKNESS = 0.25;

const POSITIONS = new Float32Array([
    -0.5,
    -THICKNESS / 2,
    0,
    0.5,
    -THICKNESS / 2,
    0,
    0.5,
    THICKNESS / 2,
    0,

    -0.5,
    -THICKNESS / 2,
    0,
    0.5,
    THICKNESS / 2,
    0,
    -0.5,
    THICKNESS / 2,
    0,

    -THICKNESS / 2,
    -0.5,
    0,
    THICKNESS / 2,
    -0.5,
    0,
    THICKNESS / 2,
    0.5,
    0,

    -THICKNESS / 2,
    -0.5,
    0,
    THICKNESS / 2,
    0.5,
    0,
    -THICKNESS / 2,
    0.5,
    0
]);

const material = new MeshBasicMaterial({
    color: '#ffffff',
    side: DoubleSide
});

const matrix = new Matrix4();
const color = new Color();

interface ClampDimensionProps {
    mid: Vector3;
    floorZ: number;
    selectedPoiIndex: number;
}

export const ClampDimension = ({ mid, floorZ, selectedPoiIndex }: ClampDimensionProps) => {
    const { workingPlane, articleColliders, articleGroups, poi, instanceToGroupMap } = usePlacementStore(
        useShallow((state) => ({
            articleColliders: state.colliders.article,
            workingPlane: state.workingPlane,
            articleGroups: state.articleGroups,
            poi: state.pois[selectedPoiIndex],
            instanceToGroupMap: state.instanceToGroupMap
        }))
    );

    const { markers } = useCreateClampDimensioning({
        instanceToGroupMap,
        articleColliders,
        selectedPoiIndex,
        mid,
        workingPlane,
        floorZ,
        poi
    });

    const groupColors = useMemo(
        () => Object.fromEntries(Object.entries(articleGroups).map(([id, group]) => [id, group.color])),
        [articleGroups]
    );

    const geometry = useMemo(() => {
        const geom = new BufferGeometry();
        geom.setAttribute('position', new BufferAttribute(POSITIONS.slice(), 3));
        geom.computeBoundingSphere();
        return geom;
    }, []);

    const clampLayers = useMemo(() => {
        const layers = new Map<number, DimensionClamp[]>();

        for (const marker of markers) {
            const existing = layers.get(marker.renderOrder);

            if (existing) {
                existing.push(marker);
            } else {
                layers.set(marker.renderOrder, [marker]);
            }
        }

        return [...layers.entries()].sort((a, b) => a[0] - b[0]);
    }, [markers]);

    const handleOnClampDimensionRef = useCallback(
        (clamps: DimensionClamp[]) => (instance: InstancedMesh | null) => {
            if (!instance) {
                return;
            }

            clamps.forEach((marker, index) => {
                const px = marker.center.x + marker.normal.x * MARKER_OFFSET;
                const py = marker.center.y + marker.normal.y * MARKER_OFFSET;
                const pz = marker.center.z + marker.normal.z * MARKER_OFFSET;

                matrix.set(
                    marker.u.x * marker.width,
                    marker.v.x * marker.height,
                    marker.normal.x,
                    px,
                    marker.u.y * marker.width,
                    marker.v.y * marker.height,
                    marker.normal.y,
                    py,
                    marker.u.z * marker.width,
                    marker.v.z * marker.height,
                    marker.normal.z,
                    pz,
                    0,
                    0,
                    0,
                    1
                );

                instance.setMatrixAt(index, matrix);

                // Apply color
                const hex = marker.groupId != null ? (groupColors[marker.groupId] ?? '#000000') : '#000000';
                color.set(hex);
                if (hex !== '#000000') {
                    // Use higher contrast to default group color
                    const hsl = { h: 0, s: 0, l: 0 };
                    color.getHSL(hsl);
                    color.setHSL(hsl.h, Math.min(hsl.s * 2.5, 1), Math.max(hsl.l * 0.4, 0.1));
                }
                instance.setColorAt(index, color);
            });

            instance.instanceMatrix.needsUpdate = true;

            if (instance.instanceColor) {
                instance.instanceColor.needsUpdate = true;
            }

            invalidate();
        },
        [groupColors]
    );

    useEffect(() => {
        return () => {
            geometry.dispose();
            material.dispose();
        };
    }, [geometry]);

    if (clampLayers.length === 0) {
        return null;
    }

    return (
        <>
            {clampLayers.map(([renderOrder, clamps]) => (
                <instancedMesh
                    key={renderOrder}
                    ref={handleOnClampDimensionRef(clamps)}
                    args={[geometry, material, clamps.length]}
                    frustumCulled={false}
                    renderOrder={renderOrder}
                />
            ))}
        </>
    );
};
